import { BookmarkSubCategory } from '@softapps/core';
import { MasterdataSupplier } from '../types/masterdata-entities';
import {
  defineMasterdataEntity,
  MasterdataBadge,
  MasterdataFilter,
} from '../types';

/** Full supplier name (salutation + name1 + name2 + name3), trimmed. */
function supplierName(s: MasterdataSupplier): string {
  return [s.salutation, s.name1, s.name2, s.name3].filter(Boolean).join(' ').trim();
}

/** Supplier number with optional subnumber (`123` or `123/4`). */
function supplierNumberLabel(s: MasterdataSupplier): string {
  return s.supplierSubnumber > 0
    ? `${s.supplierNumber}/${s.supplierSubnumber}`
    : `${s.supplierNumber ?? ''}`;
}

/** Blocked-status (Sperrkennzeichen) badge; 'N'/absent renders no badge. */
function blockedBadge(s: MasterdataSupplier): MasterdataBadge | null {
  switch (s.blockedStatus) {
    case 'J':
      return { label: 'Gesperrt', tone: 'danger' };
    case 'W':
      return { label: 'Warnung', tone: 'warning' };
    case 'D':
      return { label: 'DSGVO-Löschantrag', tone: 'danger' };
    default:
      return null;
  }
}

/** `code - name` for the group columns; whichever half is missing is dropped. */
function groupLabel(code: string | null, name: string | null): string | null {
  if (code && name) return `${code} - ${name}`;
  return code ?? name ?? null;
}

/**
 * Supplier (Lieferant) masterdata record — not the product's supplier list.
 * `id`/`category` are not filterable: the `[entityId]` contract is the supplier-number
 * label (`14227` or `14227/2`), resolved by `idFilter` and returned by `trackWith`.
 * The search index contains deleted suppliers — deleted=false is mandatory (API doc).
 */
export const MASTERDATA_SUPPLIER = defineMasterdataEntity<MasterdataSupplier>({
  name: 'supplier',
  responseKey: 'suppliers',
  defaultFilter: [{ type: 'booleanIsFalse', name: 'deleted' }],
  displayWith: (s) => {
    const name = supplierName(s);
    const number = supplierNumberLabel(s);
    return name ? `${number} - ${name}` : number;
  },
  secondaryWith: (s) => [s.postCode, s.city].filter(Boolean).join(' ') || null,
  trackWith: (s) => supplierNumberLabel(s),
  keyWith: (s) => supplierNumberLabel(s),
  idFilter: (id): MasterdataFilter => {
    const [number, subnumber] = String(id).split('/');
    return {
      type: 'conditions',
      operator: 'AND',
      filters: [
        { type: 'numberEquals', name: 'supplierNumber', value: Number(number) },
        { type: 'numberEquals', name: 'supplierSubnumber', value: Number(subnumber ?? 0) },
      ],
    };
  },
  badgeWith: blockedBadge,
  bookmarkSubCategory: BookmarkSubCategory.Supplier,
  icon: 'fa-regular fa-truck-fast',
  title: 'Lieferantensuche',
  label: 'Lieferant',
  placeholder: 'Lieferant suchen...',
  columns: [
    {
      label: 'Lieferant',
      type: 'entity',
      icon: 'fa-regular fa-truck-fast',
      value: (s) => supplierName(s),
      secondary: (s) => [s.postCode, s.city].filter(Boolean).join(' ') || null,
    },
    { label: 'Nr.', value: (s) => supplierNumberLabel(s) },

    { label: 'Straße', value: (s) => s.street },
    { label: 'Hausnummer', value: (s) => s.streetNumber },
    { label: 'PLZ', value: (s) => s.postCode },
    { label: 'Ort', value: (s) => s.city },
    { label: 'Land', value: (s) => s.countryCode },
    { label: 'Telefon', value: (s) => s.phone },
    { label: 'E-Mail', value: (s) => s.mail1 },
    { label: 'UID', value: (s) => s.uid },

    { label: 'Kurzbezeichnung', value: (s) => s.shortName },
    { label: 'Sperre', type: 'badge', badge: blockedBadge },
    { label: 'Hauptgruppe', value: (s) => groupLabel(s.mainGroupCode, s.mainGroupName) },
    { label: 'Untergruppe', value: (s) => groupLabel(s.subGroupCode, s.subGroupName) },
    {
      label: 'Unteruntergruppe',
      value: (s) => groupLabel(s.subSubGroupCode, s.subSubGroupName),
    },
  ],
});
